import React, { useEffect, useState, useRef } from 'react';
import { Target, User, ListChecks, Clock, Plus, X, Check, Briefcase, Flag, FlaskConical } from 'lucide-react';
import { loadSettings, saveSettings } from '../lib/settings';
interface Habit {
  id: number;
  text: string;
}
export function Settings() {
  const initial = loadSettings();
  const [name, setName] = useState(initial.name);
  const [goal, setGoal] = useState(initial.goal);
  const [habits, setHabits] = useState<Habit[]>(initial.habits.map((text, i) => ({
    id: i + 1,
    text
  })));
  const [ppHours, setPpHours] = useState(initial.ppHours);
  const [ppMinutes, setPpMinutes] = useState(initial.ppMinutes);
  const [projects, setProjects] = useState<[string, string, string]>(initial.projects ?? ['', '', '']);
  const [ninetyDayChallenge, setNinetyDayChallenge] = useState(initial.ninetyDayChallenge ?? '');
  const [thirtyDayExperiment, setThirtyDayExperiment] = useState(initial.thirtyDayExperiment ?? '');
  const [saved, setSaved] = useState(false);
  const updateProject = (idx: 0 | 1 | 2, value: string) => {
    setProjects(prev => {
      const next = [...prev] as [string, string, string];
      next[idx] = value;
      return next;
    });
  };
  const MAX_HABITS = 12;
  const updateHabit = (id: number, text: string) => {
    setHabits(habits.map(h => h.id === id ? {
      ...h,
      text
    } : h));
  };
  const removeHabit = (id: number) => {
    setHabits(habits.filter(h => h.id !== id));
  };
  const addHabit = () => {
    if (habits.length >= MAX_HABITS) return;
    const newId = habits.length ? Math.max(...habits.map(h => h.id)) + 1 : 1;
    setHabits([...habits, {
      id: newId,
      text: ''
    }]);
  };
  const persist = () => {
    saveSettings({
      name,
      goal,
      habits: habits.map(h => h.text).filter(t => t.trim().length > 0),
      ppHours,
      ppMinutes,
      projects,
      ninetyDayChallenge,
      thirtyDayExperiment
    });
  };
  // Auto-save (debounced) — persists on every change so nothing is lost
  // if the user navigates away without pressing Save.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const t = window.setTimeout(persist, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, goal, habits, ppHours, ppMinutes, projects, ninetyDayChallenge, thirtyDayExperiment]);
  const handleSave = () => {
    persist();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <div className="max-w-2xl mx-auto p-8 pb-24" data-id="element-304">
      {/* Header */}
      <div className="mb-10" data-id="element-305">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-306">Settings</h1>
        <p className="text-gray-500" data-id="element-307">
          Configure your identity, goal, and what you'll track each day.
        </p>
      </div>

      <div className="space-y-6" data-id="element-308">
        {/* Name */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-309">
          <div className="flex items-center gap-2 mb-4" data-id="element-310">
            <div className="p-1.5 bg-gray-50 rounded-lg" data-id="element-311">
              <User className="w-4 h-4 text-gray-600" data-id="element-312" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-313">Your Name</h2>
          </div>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="What should we call you?" className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm" data-id="element-314" />
          <p className="text-xs text-gray-400 mt-2" data-id="element-315">
            Shown in your daily greeting on the Journal page.
          </p>
        </div>

        {/* Goal */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-316">
          <div className="flex items-center gap-2 mb-4" data-id="element-317">
            <div className="p-1.5 bg-indigo-50 rounded-lg" data-id="element-318">
              <Target className="w-4 h-4 text-indigo-500" data-id="element-319" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-320">
              Current Goal
            </h2>
          </div>
          <input type="text" value={goal} onChange={e => setGoal(e.target.value)} placeholder="What's the one thing you're building toward?" className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm" data-id="element-321" />
          <p className="text-xs text-gray-400 mt-2" data-id="element-322">
            Anchored at the top of your daily Reflection.
          </p>
        </div>

        {/* Habits */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-323">
          <div className="flex items-center justify-between mb-4" data-id="element-324">
            <div className="flex items-center gap-2" data-id="element-325">
              <div className="p-1.5 bg-emerald-50 rounded-lg" data-id="element-326">
                <ListChecks className="w-4 h-4 text-emerald-500" data-id="element-327" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-328">
                Daily Habits
              </h2>
            </div>
            <span className="text-xs font-medium text-gray-400" data-id="element-329">
              {habits.length} / {MAX_HABITS}
            </span>
          </div>

          <div className="space-y-2 mb-4" data-id="element-330">
            {habits.map((habit, idx) => <div key={habit.id} className="flex items-center gap-2 group" data-id="element-331">
                <span className="text-xs font-medium text-gray-400 w-5 text-center" data-id="element-332">
                  {idx + 1}
                </span>
                <input type="text" value={habit.text} onChange={e => updateHabit(habit.id, e.target.value)} placeholder="e.g. 30 min reading" className="flex-1 p-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none shadow-sm" data-id="element-333" />
                <button onClick={() => removeHabit(habit.id)} className="p-2 text-gray-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100" aria-label="Remove habit" data-id="element-334">
                  <X className="w-4 h-4" data-id="element-335" />
                </button>
              </div>)}
          </div>

          <button onClick={addHabit} disabled={habits.length >= MAX_HABITS} className="w-full py-2.5 border border-dashed border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:bg-transparent text-sm font-medium text-gray-500 hover:text-emerald-600 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-336">
            <Plus className="w-4 h-4" data-id="element-337" />
            {habits.length >= MAX_HABITS ? 'Maximum 12 habits' : 'Add a habit'}
          </button>
        </div>

        {/* Project Time */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-338">
          <div className="flex items-center gap-2 mb-4" data-id="element-339">
            <div className="p-1.5 bg-indigo-50 rounded-lg" data-id="element-340">
              <Clock className="w-4 h-4 text-indigo-500" data-id="element-341" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-342">
              Daily Project Time Goal
            </h2>
          </div>
          <p className="text-xs text-gray-500 mb-4" data-id="element-343">
            How much deep work do you want to dedicate to your project each day?
          </p>

          <div className="flex items-end gap-4" data-id="element-344">
            <div className="flex-1" data-id="element-345">
              <label className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-346">
                Hours
              </label>
              <input type="number" min={0} max={24} value={ppHours} onChange={e => setPpHours(Math.max(0, Math.min(24, Number(e.target.value))))} className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm" data-id="element-347" />
            </div>
            <div className="flex-1" data-id="element-348">
              <label className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-349">
                Minutes
              </label>
              <input type="number" min={0} max={59} step={5} value={ppMinutes} onChange={e => setPpMinutes(Math.max(0, Math.min(59, Number(e.target.value))))} className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm" data-id="element-350" />
            </div>
            <div className="px-4 py-3.5 bg-indigo-50 rounded-xl text-sm font-semibold text-indigo-600 whitespace-nowrap" data-id="element-351">
              {ppHours}h {ppMinutes}m / day
            </div>
          </div>
        </div>

        {/* Daily Projects */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-352">
          <div className="flex items-center gap-2 mb-4" data-id="element-353">
            <div className="p-1.5 bg-indigo-50 rounded-lg" data-id="element-354">
              <Briefcase className="w-4 h-4 text-indigo-500" data-id="element-355" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-356">
              Daily Projects
            </h2>
          </div>
          <p className="text-xs text-gray-500 mb-4" data-id="element-357">
            The 3 projects you want to focus on today. Leave blank to skip.
          </p>

          <div className="space-y-2" data-id="element-358">
            {projects.map((project, idx) => <div key={idx} className="flex items-center gap-2" data-id="element-359">
                <span className="text-xs font-medium text-gray-400 w-5 text-center" data-id="element-360">
                  {idx + 1}
                </span>
                <input type="text" value={project} onChange={e => updateProject(idx as 0 | 1 | 2, e.target.value)} placeholder={`Project ${idx + 1} (e.g. Marketing landing page)`} className="flex-1 p-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm" data-id="element-361" />
              </div>)}
          </div>
        </div>

        {/* 90-Day Challenge */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-362">
          <div className="flex items-center gap-2 mb-4" data-id="element-363">
            <div className="p-1.5 bg-emerald-50 rounded-lg" data-id="element-364">
              <Flag className="w-4 h-4 text-emerald-500" data-id="element-365" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-366">
              90-Day Challenge
            </h2>
          </div>
          <input type="text" value={ninetyDayChallenge} onChange={e => setNinetyDayChallenge(e.target.value)} placeholder="What 90-day transformation are you committing to?" className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none shadow-sm" data-id="element-367" />
          <p className="text-xs text-gray-400 mt-2" data-id="element-368">
            Night & Day difference you're pursuing this season.
          </p>
        </div>

        {/* 30-Day Experiment */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-369">
          <div className="flex items-center gap-2 mb-4" data-id="element-370">
            <div className="p-1.5 bg-indigo-50 rounded-lg" data-id="element-371">
              <FlaskConical className="w-4 h-4 text-indigo-500" data-id="element-372" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-373">
              30-Day Experiment
            </h2>
          </div>
          <input type="text" value={thirtyDayExperiment} onChange={e => setThirtyDayExperiment(e.target.value)} placeholder="What's the one experiment you're running this month?" className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm" data-id="element-374" />
          <p className="text-xs text-gray-400 mt-2" data-id="element-375">
            A focused experiment that supports your 90-day challenge.
          </p>
        </div>

        {/* Save */}
        <button onClick={handleSave} className="w-full py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2 mt-8" data-id="element-376">
          {saved ? <>
              <Check className="w-4 h-4" data-id="element-377" />
              Saved
            </> : 'Save Settings'}
        </button>
      </div>
    </div>;
}